import { BaseApplicationService } from "../../../domain/base/base-application-service";
import { UserEntity } from "../../../domain/user/user-entity";
import { toast } from "react-toastify";
import { reportUnexpectedError } from "../../../../error-reporter";
import apiClient from "../../../apis/tenant-management/tenant-management-client";
import { Group } from "../../../domain/group/group";
import { User } from "../../../domain/user/user";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../graphql/client";

export type UserDetailServiceProps = {
  isLoading: boolean;
  userId: string;
  user?: UserEntity;
  groups: Group[];
  setUserId: typeof UserDetailService.prototype.setUserId;
  fetchUser: typeof UserDetailService.prototype.fetchUser;
  fetchGroupCandidates: typeof UserDetailService.prototype.fetchGroupCandidates;
  updateUser: typeof UserDetailService.prototype.updateUser;
  hasOrgSAMLSetting: typeof UserDetailService.prototype.hasOrgSAMLSetting;
};

export class UserDetailService extends BaseApplicationService<UserDetailServiceProps> {
  // ユーザーが所属しているグループの表示件数上限
  private readonly groupDisplayCount = 100;

  state = {
    isLoading: false,
    userId: "",
    user: undefined as UserEntity | undefined,
    groups: [],
    setUserId: this.setUserId.bind(this),
    fetchUser: this.fetchUser.bind(this),
    fetchGroupCandidates: this.fetchGroupCandidates.bind(this),
    updateUser: this.updateUser.bind(this),
    hasOrgSAMLSetting: this.hasOrgSAMLSetting.bind(this),
  };

  setUserId(id: string) {
    this.setState({
      userId: id,
    });
  }

  async fetchUser() {
    this.setIsLoading(true);
    try {
      const { data: user } = await apiClient.fetchUser(this.state.userId);
      const { data: groups } = await apiClient.fetchGroups(
        1,
        // TODO: 100件を超えてグループに所属している場合、取得できなかったグループは表示されない。
        // グループの所属数の上限は20としているので現時点では定数で問題ない。
        this.groupDisplayCount,
        {
          ids: user.groupIDs,
        },
      );
      this.setState({ user, groups });
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("ユーザー情報の取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async fetchGroupCandidates(name: string) {
    const { data: groups } = await apiClient.fetchGroups(
      1,
      // TODO: 100件を超えてグループに所属している場合、取得できなかったグループは表示されない。
      // 現実問題100以上のグループに所属しているケースが無いので一旦定数で処理している。
      this.groupDisplayCount,
      {
        name,
      },
    );
    return groups;
  }

  async updateUser(user: User) {
    this.setIsLoading(true);
    try {
      await apiClient.updateUser(user);
      toast.success("ユーザーの更新に成功しました。");
      return true;
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("ユーザーの更新に失敗しました。");
      return false;
    } finally {
      this.setIsLoading(false);
    }
  }

  async hasOrgSAMLSetting(orgID: string) {
    this.setIsLoading(true);
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.orgSamlSetting({ orgID }, gqlHeader("q")),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data?.orgSamlSetting) {
        return true;
      }

      return false;
    } catch (e) {
      toast.error("SAML設定の取得に失敗しました。");
      reportUnexpectedError(e);
      return false;
    } finally {
      this.setIsLoading(false);
    }
  }

  private setIsLoading(isLoading: boolean) {
    this.setState({ isLoading: isLoading });
  }
}
